/**
 * ICONTACTIC cart behaviour (W-129, cart v2 W-170).
 *
 * 1. Keeps the complimentary eBook in the cart: added once there is something paid in the cart, removed when
 *    the cart is otherwise empty, and clamped to a single copy. Verified 2026-09-07 that a $0 line does not
 *    satisfy the minimum quantity on the "Bundle & save" automatic discounts, so the gift cannot pull a
 *    one-item order into a volume tier.
 * 2. Wires the cart upsell: the "Add" button, and the in-place variant picker behind "Choose" (W-158).
 * 3. Wires the shipping-protection toggle (cart v2). The add-on is a service line: it never counts toward the
 *    badge or the "paid" count, it is clamped to one copy, and it leaves the cart together with the last paid item.
 * 4. "Reserved for mm:ss" countdown in the drawer header: one deadline per browser session, restarts at zero.
 *
 * Loaded once from layout/theme.liquid, so it survives the section re-renders that replace the cart markup.
 */
(function () {
  var cfg = window.ICT_CART || {};
  if (window.__ictCartReady) return;
  window.__ictCartReady = true;

  var busy = false;

  function readCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); });
  }

  /** Gift and protection are service lines: the shopper did not pick them as products. */
  function isService(item) {
    return (cfg.giftHandle && item.handle === cfg.giftHandle) || (cfg.protectHandle && item.handle === cfg.protectHandle);
  }

  /** The badge counts what the shopper chose, so service lines stay out of it. */
  function paidCount(cart) {
    if (!cart) return undefined;
    return cart.items.reduce(function (n, item) { return isService(item) ? n : n + item.quantity; }, 0);
  }

  function announce(cart) {
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: { data: { source: 'icontactic-cart', itemCount: paidCount(cart) } },
      })
    );
  }

  function linesFor(cart, handle) {
    var out = [];
    if (!handle) return out;
    cart.items.forEach(function (item, i) {
      if (item.handle === handle) out.push({ line: i + 1, quantity: item.quantity });
    });
    return out;
  }

  function change(body) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then(function (r) { return r.json(); });
  }

  function add(id) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] }),
    }).then(function (r) { return r.json(); });
  }

  /**
   * Brings the cart in line with the gift and protection rules. Idempotent: a settled cart makes no request.
   * One fix per pass, then another pass: removing the last paid item has to take out both the protection and the
   * gift, and our own announce is ignored by the cart:update listener, so the follow-up pass is started here.
   */
  function reconcile(pass) {
    pass = pass || 0;
    if (busy || pass > 4) return Promise.resolve();
    busy = true;
    var changed = false;
    function did(p) { changed = true; return p; }
    return readCart()
      .then(function (cart) {
        var gifts = linesFor(cart, cfg.giftHandle);
        var protects = linesFor(cart, cfg.protectHandle);
        var paid = cart.items.length - gifts.length - protects.length;

        if (paid === 0 && protects.length > 0) {
          return did(change({ line: protects[0].line, quantity: 0 }).then(announce));
        }
        if (protects.length > 0 && protects[0].quantity !== 1) {
          return did(change({ line: protects[0].line, quantity: 1 }).then(announce));
        }
        if (!cfg.giftEnabled || !cfg.giftVariantId || !cfg.giftHandle) return null;
        if (paid === 0 && gifts.length > 0) {
          return did(change({ line: gifts[0].line, quantity: 0 }).then(announce));
        }
        if (paid > 0 && gifts.length === 0) {
          return did(add(cfg.giftVariantId).then(function () { return readCart(); }).then(announce));
        }
        if (gifts.length > 0 && gifts[0].quantity !== 1) {
          return did(change({ line: gifts[0].line, quantity: 1 }).then(announce));
        }
        return null;
      })
      .catch(function () { /* a failed reconcile must never block the cart */ })
      .then(function () {
        busy = false;
        if (changed) return reconcile(pass + 1);
      });
  }

  document.addEventListener('cart:update', function (event) {
    var detail = event.detail || {};
    var data = detail.data || {};
    if (data.source === 'icontactic-cart') return;
    reconcile();
  });

  // W-158: options open inside the cart. "Choose" reveals the variants that are in stock, picking one arms the
  // Add button with that variant id, and nothing navigates away from the cart the shopper already built.
  document.addEventListener('click', function (event) {
    var target = event.target.closest && event.target.closest('[data-ict-upsell-open]');
    if (!target) return;
    event.preventDefault();
    var box = target.closest('.ict-cart-upsell');
    var opts = box && box.querySelector('[data-ict-upsell-opts]');
    if (!opts) return;
    opts.hidden = false;
    target.hidden = true;
    target.setAttribute('aria-expanded', 'true');
    var first = opts.querySelector('[data-ict-variant]');
    if (first) first.focus();
  });

  function upsellVariants(box) {
    var tag = box.querySelector('[data-ict-upsell-variants]');
    if (!tag) return [];
    try { return JSON.parse(tag.textContent) || []; } catch (e) { return []; }
  }

  function chosenOptions(box) {
    var out = [];
    var rows = box.querySelectorAll('[data-ict-opt]');
    for (var i = 0; i < rows.length; i++) {
      var picked = rows[i].querySelector('[aria-checked="true"]');
      out.push(picked ? picked.getAttribute('data-ict-opt-value') : null);
    }
    return out;
  }

  // A value is offerable when at least one available variant carries it alongside everything already picked.
  function refreshUpsell(box) {
    var variants = upsellVariants(box);
    var picked = chosenOptions(box);
    var chips = box.querySelectorAll('[data-ict-opt-value]');
    for (var i = 0; i < chips.length; i++) {
      var chip = chips[i];
      var index = Number(chip.getAttribute('data-ict-opt-index'));
      var value = chip.getAttribute('data-ict-opt-value');
      var reachable = variants.some(function (variant) {
        if (variant.opts[index] !== value) return false;
        return picked.every(function (chosen, at) {
          return at === index || !chosen || variant.opts[at] === chosen;
        });
      });
      chip.disabled = !reachable;
      chip.classList.toggle('is-out', !reachable);
    }

    var complete = picked.every(function (value) { return !!value; });
    var match = complete
      ? variants.filter(function (variant) {
          return variant.opts.every(function (value, at) { return value === picked[at]; });
        })[0]
      : null;

    var confirm = box.querySelector('.ict-cart-upsell__confirm');
    if (confirm) {
      confirm.setAttribute('data-ict-upsell-add', match ? String(match.id) : '');
      confirm.disabled = !match;
    }
    var price = box.querySelector('.ict-cart-upsell__price');
    if (price && match && match.money) price.textContent = match.money;
  }

  document.addEventListener('click', function (event) {
    var chip = event.target.closest && event.target.closest('[data-ict-opt-value]');
    if (!chip || chip.disabled) return;
    event.preventDefault();
    var row = chip.closest('[data-ict-opt]');
    var box = chip.closest('.ict-cart-upsell');
    if (!row || !box) return;
    var siblings = row.querySelectorAll('[data-ict-opt-value]');
    for (var i = 0; i < siblings.length; i++) {
      siblings[i].setAttribute('aria-checked', siblings[i] === chip ? 'true' : 'false');
    }
    refreshUpsell(box);
  });

  document.addEventListener('click', function (event) {
    var button = event.target.closest && event.target.closest('[data-ict-upsell-add]');
    if (!button || button.disabled) return;
    event.preventDefault();
    var id = Number(button.getAttribute('data-ict-upsell-add'));
    if (!id) return;
    button.disabled = true;
    button.classList.add('is-loading');
    add(id)
      .then(function () { return readCart(); })
      .then(announce)
      .catch(function () { button.disabled = false; button.classList.remove('is-loading'); });
  });

  /* Protection toggle. On = add one; off = remove the line. The drawer re-renders from the server, so the
     switch always reflects the cart. */
  document.addEventListener('click', function (event) {
    var sw = event.target.closest && event.target.closest('[data-ict-protect-toggle]');
    if (!sw || sw.disabled) return;
    event.preventDefault();
    var id = Number(sw.getAttribute('data-ict-protect-toggle'));
    if (!id) return;
    var on = sw.getAttribute('aria-checked') === 'true';
    sw.disabled = true;
    sw.setAttribute('aria-checked', on ? 'false' : 'true');
    sw.classList.add('is-loading');
    var run = on
      ? readCart().then(function (cart) {
          var lines = linesFor(cart, cfg.protectHandle);
          return lines.length ? change({ line: lines[0].line, quantity: 0 }) : cart;
        })
      : add(id);
    run
      .then(function () { return readCart(); })
      .then(announce)
      .catch(function () { sw.disabled = false; sw.classList.remove('is-loading'); sw.setAttribute('aria-checked', on ? 'true' : 'false'); });
  });

  /* "Reserved for mm:ss". One deadline per browser session; the ticker writes into whatever
     [data-ict-hold-time] exists at that second, so the drawer can re-render freely. At zero it restarts. */
  (function () {
    var KEY = 'ictHoldUntil';
    var MINUTES = Number(document.documentElement.getAttribute('data-ict-hold-minutes') || 10);
    function pad(n) { return (n < 10 ? '0' : '') + n; }
    function tick() {
      var els = document.querySelectorAll('[data-ict-hold]');
      if (!els.length) return;
      var until = Number(sessionStorage.getItem(KEY) || 0);
      if (!until) { until = Date.now() + MINUTES * 60000; sessionStorage.setItem(KEY, String(until)); }
      var left = Math.round((until - Date.now()) / 1000);
      if (left <= 0) { until = Date.now() + MINUTES * 60000; sessionStorage.setItem(KEY, String(until)); left = MINUTES * 60; }
      var m = Math.floor(left / 60), s = left % 60;
      els.forEach(function (el) {
        var t = el.querySelector('[data-ict-hold-time]');
        if (t) t.textContent = pad(m) + ':' + pad(s);
      });
    }
    try { tick(); setInterval(tick, 1000); } catch (e) { /* sessionStorage blocked: no timer */ }
    document.addEventListener('cart:update', function () {
      readCart().then(function (cart) { if (!cart.items.length) sessionStorage.removeItem(KEY); }).catch(function () {});
    });
  })();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { reconcile(); });
  } else {
    reconcile();
  }
})();
